import { zodResolver } from '@hookform/resolvers/zod';
import { DialogContent, DialogTitle, Divider } from '@mui/material';
import type { ContractTerminationOptions } from '@weft/api-definition';
import { CONTRACT_TERMINATION_OPTIONS } from '@weft/api-definition';
import { NewPlanSchema } from '@weft/types';
import { withDefault } from '@weft/utils';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { useContract } from 'apps/app/src/hooks/requests/use-contract';
import { useReviseContract } from 'apps/app/src/hooks/requests/use-revise-contract/use-revise-contract';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import type { NewPlanForm } from 'apps/app/src/pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from 'apps/app/src/pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanSettingsSelect } from 'apps/app/src/pages/plans/CreatePlanPage/create-plan-page-components/PlanSettingsSelect';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import cn from 'classnames';
import { useCallback } from 'react';
import { useFormContext, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { ContractReviseDialogActions } from '../../contract-page-components/ContractPageDialogActions';
import { getNewContractStartDateByTerminationOption, getTerminationOptionsValues } from '../contract-revise-page-utils/date-utils';

type SaveReviseProps = {
  open: boolean;
  onClose?: () => void;
};

export const ReviseCurrentContractFormSchema = z.object({
  terminationOption: z.nativeEnum(CONTRACT_TERMINATION_OPTIONS),
});

export type ReviseCurrentContractForm = z.infer<typeof ReviseCurrentContractFormSchema>;

export const SaveReviseCurrentDialog: React.FC<SaveReviseProps> = ({
  open,
  onClose,
}) => {
  const { contractId } = useValidatedParams('contractId');
  const queryClient = useQueryClient();

  const navigate = useVyNavigate();
  const { watch, formState: { errors } } = useFormContext<PlanInputForm, NewPlanForm>();

  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const form = useForm<ReviseCurrentContractForm>({
    resolver: zodResolver(ReviseCurrentContractFormSchema),
  });

  const reviseContractMutation = useReviseContract();

  const billingInterval = contract.plan.billingData.billingInterval;
  const contractStartDate = contract.startDate;

  const handleSubmit = form.handleSubmit(async ({ terminationOption }) => {
    const newContractStartDate = getNewContractStartDateByTerminationOption(terminationOption, contractStartDate, billingInterval);
    const planInput = watch();
    const fixedProducts = withDefault(planInput?.fixedProducts, []);
    const usageProducts = withDefault(planInput?.usageProducts, []);
    const products = [...fixedProducts, ...usageProducts];

    const parsedNewPlan = NewPlanSchema.safeParse({
      ...planInput,
      products,
    });
    
    if (!parsedNewPlan.success) {
      return;
    }

    await runMutation(
      reviseContractMutation,
      {
        contractId,
        reviseContractRequest: {
          startDate: newContractStartDate,
          plan: parsedNewPlan.data,
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to revise contract',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(contractId),
          });

          handleClose();
          navigate(`/plans/contracts/${contractId}`);
        },
      },
    );
  });

  const { ref: terminationOptionRef, ...terminationOptionRegister } = form.register('terminationOption');
  const handleTerminationOptionChanged = useCallback((terminationOption: ContractTerminationOptions) => {
    form.setValue('terminationOption', terminationOption);
  }, [form]);
  
  const handleClose = () => {
    onClose?.();
    form.reset();
  };

  return (
    <>
      <DefaultThemeDialog
        open={open}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          className: 'max-w-[780px]',
        }}
      >
        <DialogTitle>Update current contract</DialogTitle>

        <DialogContent>
          <Divider className="my-6" />

          <div className="flex flex-col gap-y-4">
            <div className="flex">
              <PlanSettingsSelect
                label='Effective of'
                items={getTerminationOptionsValues(getNewContractStartDateByTerminationOption(CONTRACT_TERMINATION_OPTIONS.EndOfBillingCycle, contractStartDate, billingInterval))}
                value={form.watch('terminationOption')}
                {...terminationOptionRegister}
                onChange={handleTerminationOptionChanged}
                className={cn('bg-input', { 'border border-input-error': errors.billingData?.duration })}
                errorMessage={errors.billingData?.duration?.message}
              />
            </div>
            <Divider className="my-6"/>
            <ContractReviseDialogActions
              handleSubmit={handleSubmit}
              handleClose={handleClose}
              text="Renew"
            />
          </div>
        </DialogContent>

      </DefaultThemeDialog>
    </>
  );
};